import { Amplify } from 'aws-amplify';
/*
 * Registers the handler that finishes a signInWithRedirect callback.
 *
 * Amplify only attaches it as an import side effect, and the code that performs the
 * token exchange is reachable only from the sign-in APIs — which live in the
 * code-split sign-in modal. So on returning from Google the page had the listener
 * but not the exchange: no request to /oauth2/token, no error, and ?code= left
 * sitting in the URL. Opening the sign-in dialog pulled that chunk in and the
 * pending flow then completed, which is why signing in appeared to need two
 * attempts. This module is imported eagerly from src/index.tsx, so importing the
 * listener here puts the whole completion path in the initial bundle.
 *
 * Must stay a bare side-effect import: there is no binding to use, and dropping it
 * silently breaks federated sign-in.
 */
import 'aws-amplify/auth/enable-oauth-listener';

/**
 * Configures Amplify Auth as an import side effect.
 *
 * This used to live at module scope in LocalingosAuthenticator.tsx, which worked
 * only because that component — and with it the whole Amplify UI and Cloudscape
 * form stack — was imported eagerly on every page load. Now that the sign-in UI
 * is code-split behind a dynamic import, the configuration has to be somewhere
 * that is still loaded up front: `UserContext` calls `fetchAuthSession()` on mount
 * and would throw without it.
 *
 * Imported for its side effect from src/index.tsx, before the app renders.
 */
/**
 * The redirect list Amplify is given, with the dev server's own origin added when it
 * differs from the configured one.
 *
 * Amplify picks a redirect URL by matching it against the current page. From
 * getRedirectUrl in @aws-amplify/auth, it tries `redirect.startsWith(origin + pathname)`
 * and then `redirect.includes(location.hostname)`; if neither matches but an https entry
 * exists, it throws InvalidOriginException — "redirect is coming from a different origin.
 * The oauth flow needs to be initiated from the same origin".
 *
 * That is exactly what happens when a local dev server is started against a deployed
 * stage's config. `npm run start-gamma` loads .env.gamma, whose REDIRECT_SIGN_IN is
 * https://gamma.localingos.com, so a page served from https://desktop.localingos.com:3000
 * matches nothing and every provider button fails — including Google, because the check
 * runs before the provider is even looked at. The failure names the origin but not the
 * cause, and it is indistinguishable from a genuinely misconfigured pool.
 *
 * Only outside a production build, and only additive: the configured URL stays first, so
 * the deployed site is unaffected. If the dev origin is not also a callback URL on the
 * user pool client, Cognito rejects it by name later in the flow, which is a far more
 * legible error than this one.
 */
function redirectUrls(configured: string): string[] {
    if (process.env.NODE_ENV === 'production' || typeof window === 'undefined') return [configured];

    const here = window.location.origin;
    return configured.includes(window.location.hostname) ? [configured] : [configured, here];
}

Amplify.configure({
    Auth: {
        Cognito: {
            userPoolId: process.env.REACT_APP_USER_POOL_ID!,
            userPoolClientId: process.env.REACT_APP_USER_POOL_CLIENT_ID!,
            loginWith: {
                email: true,
                username: false,
                oauth: {
                    domain: process.env.REACT_APP_AUTH_DOMAIN!,
                    scopes: ['openid', 'email', 'profile'],
                    redirectSignIn: redirectUrls(process.env.REACT_APP_REDIRECT_SIGN_IN!),
                    redirectSignOut: redirectUrls(process.env.REACT_APP_REDIRECT_SIGN_OUT!),
                    responseType: 'code',
                    providers: ['Google', { custom: 'Cognito' }]
                },
            },
        },
    },
});
